import React, { useEffect, useMemo, useState } from "react";
import profileSchema from "./json_view.json";

import {
  apiXmlData,
  updateXmlData,
} from "../../redux/actions/organizationProfileCreators";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tab, Tabs, useMediaQuery, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import ProfileInfo from "./ProfileInfo";
import { useTranslation } from "react-i18next";

/**
 * This page is responsible for:
 * - Displaying the list of steps.
 * - Dynamically calling and rendering the components for questions based on the selected step.
 *
 * It ensures that all steps are listed, and their related questions are shown when a step is selected.
 */

export default function ProfileDetails(props) {
  // consts
  // consts

  const orgaProfileData =
    useSelector((state) => state.organizationProfile.organizationMetricsById) ||
    {};
  const validationEnabled =
    typeof orgaProfileData?.validation_status === "boolean"
      ? orgaProfileData.validation_status
      : true;

  const { t } = useTranslation();
  const listOfSteps = useMemo(
    () => [
      {
        id: 1,
        identifier: "step_1",
        value: "organization_identity",
        value_fr: "Identité Organisationnelle",
      },
      {
        id: 2,
        identifier: "step_2",
        value: "organizational_characteristics",
        value_fr: "Caractéristiques Organisationnelles",
      },
      {
        id: 3,
        identifier: "step_3",
        value: "financial_profile",
        value_fr: "Profil Financier",
      },
      {
        id: 4,
        identifier: "step_4",
        value: "customer_market_profile",
        value_fr: "Profil du Marché Client",
      },
      {
        id: 5,
        identifier: "step_5",
        value: "data_information_assets",
        value_fr: "Données et Actifs d'Information",
      },
      {
        id: 6,
        identifier: "step_6",
        value: "technology_infrastructure",
        value_fr: "Infrastructure Technologique",
      },
      {
        id: 7,
        identifier: "step_7",
        value: "operational_characteristics",
        value_fr: "Caractéristiques Opérationnelles",
      },
      {
        id: 8,
        identifier: "step_8",
        value: "regulatory_compliance_environment",
        value_fr: "Environnement de Conformité Réglementaire",
      },
      {
        id: 9,
        identifier: "step_9",
        value: "security_risk_profile",
        value_fr: "Profil de Risque de Sécurité",
      },
      {
        id: 10,
        identifier: "step_10",
        value: "reputational_stakeholder_factors",
        value_fr: "Facteurs Réputationnels et Parties Prenantes",
      },
    ],
    []
  );

  // Default initial data: use template for first step when available
  const defaultStepKey = listOfSteps && listOfSteps[0] && listOfSteps[0].value;
  const templateRoot = profileSchema; // top-level keys map to step values
  // Initialize from schema for the first step when available
  const data = (templateRoot && templateRoot[defaultStepKey]) || {};

  const orga_id =
    useSelector((state) => state.organizationProfile.id) ||
    localStorage.getItem("orgaId");
  const dispatch = useDispatch();
  const storedXml = useSelector((state) => state.organizationProfile.xml_data);
  const storedXmlOrgaId = useSelector(
    (state) => state.organizationProfile.xml_data_orga_id
  );
  // The last line of defence, and the one that does not depend on any fetch
  // behaving. This panel renders a named organization's identity, so it must be
  // able to say the data in front of it belongs to that organization - whether
  // the mismatch came from a failed request, a remount holding the previous
  // org's store, or a reply that arrived out of order. A mismatch falls through
  // to the blank template below, which is the correct thing to show when the
  // answer is "we do not have this org's profile".
  const dataXml =
    String(storedXmlOrgaId ?? "") === String(orga_id ?? "") ? storedXml : null;
  const [value, setValue] = useState(0);
  const language = localStorage.getItem("i18nextLng");
  const isMobile = useMediaQuery("(max-width:1400px)");

  // theme-aware breakpoints for better responsiveness
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const isXsDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [profileData, setProfileData] = useState(data);
  const [editedByStep, setEditedByStep] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  // Format step labels from snake_case to Title Case with spaces
  const formatStepLabel = (str) => {
    if (!str || typeof str !== "string") return str;
    const trimmed = str.trim();
    return trimmed
      .split("_")
      .filter((s) => s && s.length > 0)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  };
  // derive current step key from selected tab index
  const stepKey = useMemo(
    () =>
      (listOfSteps && listOfSteps[value] && listOfSteps[value].value) ||
      undefined,
    [listOfSteps, value]
  );

  const location = useLocation();
  const propEditMode = props?.editMode;
  const editModeFromNavigation =
    typeof propEditMode !== "undefined"
      ? propEditMode
      : location?.state?.editMode ?? false;
  const editMode = validationEnabled ? editModeFromNavigation : false;

  // Local toggle for edit mode (button-controlled)
  const [isEditMode, setIsEditMode] = useState(editMode);
  useEffect(() => {
    if (!validationEnabled && isEditMode) {
      setIsEditMode(false);
    }
  }, [validationEnabled, isEditMode]);
  console.log("dataXml:", dataXml);
  // Find a value by key anywhere within an incoming nested object
  const deepFindByKey = (obj, targetKey) => {
    if (!obj || typeof obj !== "object") return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, targetKey)) {
      return obj[targetKey];
    }
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      const found = deepFindByKey(v, targetKey);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  // Normalize API incoming shapes to primitives/arrays/objects consumable by ProfileInfo
  const normalizeIncoming = (val) => {
    if (val === null || val === undefined) return undefined;
    if (Array.isArray(val)) return val;
    if (typeof val !== "object") return val;
    const keys = Object.keys(val);
    if (keys.length === 0) return [];
    if (keys.length === 1) {
      const k = keys[0];
      const inner = val[k];
      if (Array.isArray(inner)) return inner;
      if (inner === null || inner === undefined) return [];
      if (typeof inner === "object") return inner;
      return [inner];
    }
    return val;
  };

  // Merge API values into schema-like template for a given section
  const mergeSection = (schemaSection, incomingSection) => {
    if (!schemaSection) return schemaSection;
    if (!incomingSection || typeof incomingSection !== "object")
      return schemaSection;
    let out;
    try {
      out = JSON.parse(JSON.stringify(schemaSection));
    } catch (e) {
      out = { ...schemaSection };
    }

    // Ensure primitive values respect the schema type (e.g., avoid string in number fields)
    const coerceByType = (def, raw) => {
      if (!def || typeof def !== "object") return raw;
      const t = def.type;
      if (t === "number") {
        if (typeof raw === "number") return raw;
        if (typeof raw === "string") {
          const trimmed = raw.trim();
          // Only accept if the string is a valid number
          const num = trimmed !== "" ? Number(trimmed) : NaN;
          return Number.isFinite(num) ? num : undefined;
        }
        // Other types are not acceptable for number
        return undefined;
      }
      // For other types, return as-is (existing normalizeIncoming will shape it)
      return raw;
    };

    const writeValue = (def, incomingVal) => {
      if (!def || typeof def !== "object") return;
      if (
        def.type === "object" &&
        def.fields &&
        typeof incomingVal === "object" &&
        !Array.isArray(incomingVal)
      ) {
        // Store the raw object for view-mode consumption
        try {
          def.value = JSON.parse(JSON.stringify(incomingVal));
        } catch (e) {
          def.value = { ...incomingVal };
        }
        for (const fk of Object.keys(def.fields)) {
          if (Object.prototype.hasOwnProperty.call(incomingVal, fk)) {
            const v = normalizeIncoming(incomingVal[fk]);
            const fdef = def.fields[fk];
            if (
              fdef &&
              typeof fdef === "object" &&
              fdef.type === "object" &&
              fdef.fields
            ) {
              writeValue(fdef, v);
            } else {
              def.fields[fk].value = coerceByType(fdef, v);
            }
          }
        }
      } else if (def.type === "array") {
        // Arrays can be primitives or objects (when def.fields exists)
        if (def.fields) {
          // Normalize common XML shapes to an array form
          let arr = [];
          if (Array.isArray(incomingVal)) {
            arr = incomingVal;
          } else if (incomingVal && typeof incomingVal === "object") {
            const keys = Object.keys(incomingVal);
            if (keys.length === 0) {
              arr = [];
            } else if (keys.length === 1) {
              const inner = incomingVal[keys[0]];
              if (Array.isArray(inner)) arr = inner;
              else if (inner === null || inner === undefined) arr = [];
              else arr = [inner];
            } else {
              // multi-key object: treat as a single item
              arr = [incomingVal];
            }
          } else if (incomingVal !== null && incomingVal !== undefined) {
            arr = [incomingVal];
          }

          // If schema defines a single field for the array items, ensure array of objects shape for view-mode.
          // Examples:
          // - { category: "cookies" } -> [{ category: "cookies" }]
          // - ["a","b"] -> [{ category: "a" }, { category: "b" }]
          // - [{ category: "a" }, { category: "b" }] -> unchanged
          const fieldKeys = Object.keys(def.fields || {});
          if (fieldKeys.length === 1 && Array.isArray(arr)) {
            const singleKey = fieldKeys[0];
            arr = arr
              .map((item) => {
                if (item === null || item === undefined) return undefined;
                if (item && typeof item === "object" && !Array.isArray(item)) {
                  if (Object.prototype.hasOwnProperty.call(item, singleKey)) {
                    const v = item[singleKey];
                    return v === undefined || v === null
                      ? undefined
                      : { [singleKey]: v };
                  }
                  // object without the singleKey: wrap it under the key
                  return { [singleKey]: item };
                }
                // primitive: wrap under the singleKey
                return { [singleKey]: item };
              })
              .filter((v) => v !== undefined && v !== null);
          }
          def.value = arr;
        } else {
          def.value = coerceByType(def, normalizeIncoming(incomingVal));
        }
      } else {
        def.value = coerceByType(def, normalizeIncoming(incomingVal));
      }
    };

    // Ensure any array with a single item field is an array of objects consistently
    const canonicalizeArrays = (node) => {
      if (!node || typeof node !== "object") return;
      for (const k of Object.keys(node)) {
        const def = node[k];
        if (!def || typeof def !== "object") continue;
        if (Object.prototype.hasOwnProperty.call(def, "type")) {
          if (def.type === "array" && def.fields) {
            const fieldKeys = Object.keys(def.fields || {});
            if (fieldKeys.length === 1) {
              const singleKey = fieldKeys[0];
              if (Array.isArray(def.value)) {
                def.value = def.value
                  .map((item) => {
                    if (item === null || item === undefined) return undefined;
                    if (
                      item &&
                      typeof item === "object" &&
                      !Array.isArray(item)
                    ) {
                      if (
                        Object.prototype.hasOwnProperty.call(item, singleKey)
                      ) {
                        const v = item[singleKey];
                        return v === undefined || v === null
                          ? undefined
                          : { [singleKey]: v };
                      }
                      return { [singleKey]: item };
                    }
                    return { [singleKey]: item };
                  })
                  .filter((v) => v !== undefined && v !== null);
              }
            }
          } else if (def.type === "object" && def.fields) {
            canonicalizeArrays(def.fields);
          }
        } else if (def && typeof def === "object") {
          canonicalizeArrays(def);
        }
      }
    };

    for (const key of Object.keys(incomingSection)) {
      // Only merge keys that already exist in the schema; skip unknown
      if (!Object.prototype.hasOwnProperty.call(out, key)) continue;
      const incVal = incomingSection[key];
      const def = out[key];
      if (def && typeof def === "object" && def.type) {
        writeValue(def, incVal);
      } else {
        // raw group of fields (no type at section level)
        if (typeof incVal === "object" && !Array.isArray(incVal)) {
          for (const fk of Object.keys(incVal)) {
            if (
              !(out[key] && Object.prototype.hasOwnProperty.call(out[key], fk))
            )
              continue; // skip unknown field keys
            const fieldDef = out[key][fk];
            const rawVal = incVal[fk];
            // If the field has a type (e.g., array/object), delegate to writeValue to handle wrappers
            if (fieldDef && typeof fieldDef === "object" && fieldDef.type) {
              writeValue(fieldDef, rawVal);
            } else if (fieldDef && typeof fieldDef === "object") {
              const v = normalizeIncoming(rawVal);
              fieldDef.value = coerceByType(fieldDef, v);
            }
          }
        } else {
          // Primitive incoming value: only assign if schema defines a field object with type
          if (
            def &&
            typeof def === "object" &&
            Object.prototype.hasOwnProperty.call(def, "type")
          ) {
            def.value = coerceByType(def, normalizeIncoming(incVal));
          }
        }
      }
    }
    // Final pass to enforce consistent array-of-objects shapes
    try {
      canonicalizeArrays(out);
    } catch (e) {
      // noop
    }
    return out;
  };

  // functions

  function handleChange(event, newValue) {
    setValue(newValue);
    // Update displayed profile data to the selected step template (if available)
    try {
      const stepKey =
        listOfSteps && listOfSteps[newValue] && listOfSteps[newValue].value;
      if (templateRoot && stepKey && templateRoot[stepKey]) {
        const sectionTemplate = templateRoot[stepKey];
        const incomingRoot =
          dataXml && (dataXml.organization_data_profile || dataXml);
        const incomingSection = incomingRoot && incomingRoot[stepKey];
        const merged = incomingSection
          ? mergeSection(sectionTemplate, incomingSection)
          : sectionTemplate;
        setProfileData(merged);
        // capture current step's data as baseline for save
        const step = stepKey;
        setEditedByStep((prev) => ({ ...prev, [step]: merged }));
      } else {
        // keep existing profileData when no data found
      }
    } catch (err) {
      // ignore and keep existing profileData
    }
  }

  useEffect(() => {
    if (orga_id) {
      dispatch(apiXmlData(orga_id));
    }
  }, [orga_id, dispatch, stepKey]);

  // When API data or selected tab changes, merge into the section
  useEffect(() => {
    if (templateRoot && stepKey && templateRoot[stepKey]) {
      const sectionTemplate = templateRoot[stepKey];
      const incomingRoot =
        dataXml && (dataXml.organization_data_profile || dataXml);
      const incomingSection = incomingRoot && incomingRoot[stepKey];
      const merged = incomingSection
        ? mergeSection(sectionTemplate, incomingSection)
        : sectionTemplate;
      setProfileData(merged);
      setEditedByStep((prev) => ({ ...prev, [stepKey]: merged }));
    }
    // Tighten deps to stable values; listOfSteps is memoized
  }, [dataXml, stepKey, templateRoot]);

  // Extract raw values from a schema section (schema-safe)
  const extractValues = (section) => {
    if (!section || typeof section !== "object") return undefined;
    const out = {};
    for (const key of Object.keys(section)) {
      const def = section[key];
      if (!def || typeof def !== "object") continue;
      if (Object.prototype.hasOwnProperty.call(def, "type")) {
        if (def.type === "object" && def.fields) {
          // Object fields can store values in two ways:
          // 1) per-field defs via def.fields[child].value
          // 2) a single aggregated object in def.value (used by FieldObject edits)
          const objectValue =
            def && typeof def.value === "object" && def.value !== null
              ? def.value
              : undefined;

          // Prioritize def.value when present (updated by FieldObject)
          if (objectValue && Object.keys(objectValue).length) {
            out[key] = objectValue;
          } else {
            // Fallback to extracting from field definitions
            const nestedFromFields = extractValues(def.fields) || {};
            if (nestedFromFields && Object.keys(nestedFromFields).length) {
              out[key] = nestedFromFields;
            }
          }
        } else if (def.type === "array") {
          if (def.value !== undefined) {
            // If array-of-objects with a single field in schema, collapse to { fieldName: [values] }
            const fieldKeys = def.fields ? Object.keys(def.fields) : [];
            if (def.fields && fieldKeys.length === 1) {
              const singleKey = fieldKeys[0];
              const valuesArray = Array.isArray(def.value)
                ? def.value
                    .map((item) => {
                      if (
                        item &&
                        typeof item === "object" &&
                        !Array.isArray(item) &&
                        singleKey in item
                      ) {
                        return item[singleKey];
                      }
                      // If items are primitives already, keep them
                      if (
                        item !== undefined &&
                        item !== null &&
                        (typeof item !== "object" ||
                          Array.isArray(item) === false)
                      ) {
                        return item;
                      }
                      return undefined;
                    })
                    .filter((v) => v !== undefined && v !== null)
                : [];
              out[key] = { [singleKey]: valuesArray };
            } else if (def.fields && fieldKeys.length > 1) {
              // Array-of-objects with multiple fields: wrap under group_name if provided
              const groupKey = def.groupName || def.group_name || def.group;
              if (groupKey) {
                const items = Array.isArray(def.value) ? def.value : [];
                out[key] = { [groupKey]: items };
              } else {
                out[key] = def.value;
              }
            } else {
              // Primitive arrays or arrays without fields
              out[key] = def.value;
            }
          }
        } else {
          if (def.value !== undefined && def.value !== "") out[key] = def.value;
        }
      } else {
        // raw group without type: recurse into its field defs
        const nested = extractValues(def);
        if (nested && Object.keys(nested).length) out[key] = nested;
      }
    }
    return out;
  };

  const handleSectionDataChange = (nextSectionData) => {
    const stepKey =
      listOfSteps && listOfSteps[value] && listOfSteps[value].value;
    if (!stepKey) return;
    console.log(
      `[Profile] Section data updated for step "${stepKey}":`,
      nextSectionData
    );
    const updatedData = { ...editedByStep, [stepKey]: nextSectionData };
    console.log(
      "[Profile] Full editedByStep payload after update:",
      updatedData
    );
    setEditedByStep(updatedData);
  };

  const handleSave = async () => {
    try {
      // clear previous errors before attempting save
      setFieldErrors({});
      const payload = { organization_data_profile: {} };
      for (const step of listOfSteps) {
        const stepKey = step.value;
        let section = editedByStep[stepKey];
        // Fallback: if the user never opened this step, merge template with current API data
        if (!section && templateRoot && templateRoot[stepKey]) {
          const sectionTemplate = templateRoot[stepKey];
          const incomingRoot =
            dataXml && (dataXml.organization_data_profile || dataXml);
          const incomingSection = incomingRoot && incomingRoot[stepKey];
          section = incomingSection
            ? mergeSection(sectionTemplate, incomingSection)
            : sectionTemplate;
        }
        if (!section) continue;
        const raw = extractValues(section);
        if (raw && Object.keys(raw).length) {
          payload.organization_data_profile[stepKey] = raw;
        }
      }
      // Debug: inspect the exact payload shape before dispatch
      // console.log(
      //   "[ProfileDetails] Save payload:",
      //   JSON.stringify(payload, null, 2)
      // );
      const result = await dispatch(updateXmlData(payload, orga_id));

      // Only show the "no_subsectors" message when the user has actually provided a sub-sector value.
      const currentSubSectorValue =
        payload?.organization_data_profile?.organization_identity
          ?.industry_classification?.industry_sub_sector;
      const hasSubSectorValue = (() => {
        if (typeof currentSubSectorValue === "string") {
          return currentSubSectorValue.trim() !== "";
        }
        if (Array.isArray(currentSubSectorValue)) {
          return currentSubSectorValue.some((v) => {
            if (typeof v === "string") return v.trim() !== "";
            return v !== undefined && v !== null && v !== "";
          });
        }
        return (
          currentSubSectorValue !== undefined &&
          currentSubSectorValue !== null &&
          currentSubSectorValue !== ""
        );
      })();

      // Case 3 (success response): backend indicates there are no subsectors
      // Example: { message: "XML updated successfully", no_subsectors: "No sub-sectors available for this sector" }
      try {
        const noSubsectorsMsg =
          result &&
          typeof result === "object" &&
          typeof result.no_subsectors === "string"
            ? result.no_subsectors
            : undefined;

        if (
          hasSubSectorValue &&
          noSubsectorsMsg &&
          noSubsectorsMsg.trim() !== ""
        ) {
          const message =
            result && typeof result.message === "string" && result.message
              ? `${noSubsectorsMsg}`
              : noSubsectorsMsg;

          setFieldErrors((prev) => ({
            ...prev,
            // Same visible field as the validation error case
            "industry_classification.industry_sub_sector": message,
            // Full-path alias too
            "organization_identity.industry_classification.industry_sub_sector":
              message,
          }));

          // Ensure user is on the relevant step to see the message
          const stepIndex = (listOfSteps || []).findIndex(
            (s) => s.value === "organization_identity"
          );
          if (stepIndex !== -1 && value !== stepIndex) {
            setValue(stepIndex);
          }
        }
      } catch (_) {
        // noop
      }
    } catch (e) {
      // console.error("Save failed:", e);
      // Handle specific validation error for industry_sub_sector with available_subsectors
      try {
        // Case 1: industry_sub_sector required with available_subsectors
        const available = e && e.available_subsectors;
        const errMsg = e && (e.error || e.message);
        if (Array.isArray(available) && available.length) {
          const list = available.map((s) => `- ${s}`).join("\n");
          const message = `${
            errMsg ? errMsg + "\n" : ""
          }Available subsectors:\n${list}`;
          setFieldErrors((prev) => ({
            ...prev,
            // ProfileInfo paths are relative to the current step's schema,
            // so the visible field expects: "industry_classification.industry_sub_sector".
            "industry_classification.industry_sub_sector": message,
            // Keep full-path alias too (useful if later we add step-aware error lookup)
            "organization_identity.industry_classification.industry_sub_sector":
              message,
          }));
          // Ensure user is on the relevant step to see the error
          const stepIndex = (listOfSteps || []).findIndex(
            (s) => s.value === "organization_identity"
          );
          if (stepIndex !== -1 && value !== stepIndex) {
            setValue(stepIndex);
          }
        }

        // Case 2: headquarters country invalid for selected region
        const allowedCountries = e && e.allowed_countries_for_region;
        if (Array.isArray(allowedCountries) && allowedCountries.length) {
          const list = allowedCountries.map((c) => `- ${c}`).join("\n");
          const msg2 = `${
            errMsg ? errMsg + "\n" : ""
          }Allowed countries for this region:\n${list}`;

          setFieldErrors((prev) => ({
            ...prev,
            // This matches how ProfileInfo/FieldObject builds nested object paths
            // within the "organizational_characteristics" step.
            "geographic_presence.headquarters.country": msg2,
            // Optional aliases (in case other renderers expect different prefixes)
            "headquarters.country": msg2,
            "organizational_characteristics.geographic_presence.headquarters.country":
              msg2,
          }));

          // Switch to the step that contains "geographic_presence"
          const orgCharIndex = (listOfSteps || []).findIndex(
            (s) => s.value === "organizational_characteristics"
          );
          if (orgCharIndex !== -1 && value !== orgCharIndex) {
            setValue(orgCharIndex);
          }
        }
      } catch (_) {
        // noop
      }
    }
  };

  return (
    <Box
      sx={{
        padding: !isMobile ? "0px 54px" : "0px 5px",
        height: "100%",
      }}
    >
      {/* <OrgaDescription /> */}
      {/* Responsive layout: left area for tabs on wide screens, stacked on small screens */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: isSmDown ? "column" : "row",
          gap: isSmDown ? 2 : 4,
          alignItems: "flex-start",
        })}
      >
        {/* Tabs Column */}
        <Box
          sx={{
            width: isSmDown ? "100%" : 260,
            borderBottom: isSmDown ? 1 : "none",
            borderColor: "divider",
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Steps Tabs"
            variant="scrollable"
            scrollButtons="auto"
            orientation={isSmDown ? "horizontal" : "vertical"}
            TabIndicatorProps={{ style: { left: isSmDown ? undefined : 0 } }}
          >
            {(listOfSteps || []).map((step, index) => (
              <Tab
                key={step.id}
                wrapped
                disableRipple
                label={
                  language === "fr"
                    ? step.value_fr || formatStepLabel(step.value)
                    : formatStepLabel(step.value)
                }
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  textTransform: "none",
                  whiteSpace: "normal",
                  fontSize: isXsDown ? "0.75rem" : "0.9rem",
                  py: isSmDown ? 0.5 : 1,
                  // // Add slight left margin on large screens; flush on small
                  // ml: isSmDown ? 0 : 1,
                  // pl: isSmDown ? 0 : 0,
                  // px: isSmDown ? 0 : 0,
                  // mx: 0,
                  // minWidth: 0,
                  minHeight: 48,
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Content Column */}

        <Box sx={{ flex: 1 }}>
          {validationEnabled &&
            orgaProfileData?.reconnaissance_status === "completed" &&
            !orgaProfileData?.reconnaissance_validation && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Button
                  variant={isEditMode ? "outlined" : "contained"}
                  color={isEditMode ? "secondary" : "primary"}
                  onClick={() => setIsEditMode((v) => !v)}
                  size="small"
                >
                  {isEditMode ? t("disable_edit_mode") : t("enable_edit_mode")}
                </Button>
                {isEditMode && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleSave}
                  >
                    {t("save_profile")}
                  </Button>
                )}
              </Box>
            )}
          <ProfileInfo
            data={profileData}
            editMode={validationEnabled ? isEditMode : false}
            onDataChange={handleSectionDataChange}
            errors={fieldErrors}
          />
        </Box>
      </Box>
      {/* Step Details Section */}
    </Box>
  );
}
